import React, { useEffect, useState } from 'react';

import _ from 'lodash';
import cx from 'classnames';

import { isActionTriggerLoading, SPINNER_CLASS } from '../widget.helper';

import { AnimationComponent } from '../../Animations';
import { useActionExectioner } from '../core/actions';
import { HotspotTrigger } from '../../../components/hotspot';
import { useOnScreen, getHotspotWidget } from '../core/hooks';
import { getDropShadow } from '../../../services/cssHelper.service';

import { ReactSVG } from 'react-svg';

import { getColorsConfig } from '../../../components/AssetViewer/helper/cssHelper';

import styles from './shape.module.scss';

const ShapeWidgetComponent = ({ className, style, widgetProps, breakpoint, onClick }) => {
  const actionExcutioner = useActionExectioner();

  const ref = React.useRef();

  const id = _.get(widgetProps, 'uid');
  const content = _.get(widgetProps, 'content');
  const action = _.get(widgetProps, 'actions.0');

  const properties = _.get(widgetProps, `properties.${breakpoint}`);
  const defaultProperties = _.get(widgetProps, `properties.desktop`, {});
  const animation = _.get(widgetProps, `properties.${breakpoint}.animation`) || {};
  const layout = _.get(properties, 'layout');
  const fillColor = _.get(defaultProperties, `fill`);
  const stroke = _.get(defaultProperties, `stroke`);
  const flip =  _.get(properties, `flip`);
  const shadow = _.get(defaultProperties, `shadow`, {});
  const hotspotWidget = getHotspotWidget(id);

  var widgetStyle = {
    ...style,
    ...getStyles(defaultProperties)
  }

  if (action || hotspotWidget) {
    widgetStyle = {
      ...widgetStyle,
      cursor: 'pointer'
    }
  }

  const handleClick = onClick ? onClick : (e) => {
    if (action) {
      e.stopPropagation();
      actionExcutioner.execute(widgetProps, action);
    }
  }

  return (
    <HotspotTrigger widget={widgetProps} breakpoint={breakpoint} targetRef={ref}>
      <div id={id}
        ref={ref}
        className={cx(className, styles.shape, {
          [SPINNER_CLASS]: isActionTriggerLoading(action)
        })}
        style={widgetStyle}
        data-widget-id={id}
        onClick={handleClick}
      >
        <AnimationComponent {...animation}>
          <Shape
            url={content}
            width={layout.width}
            height={layout.height}
            fill={ fillColor?.color || fillColor}
            flip={flip}
            stroke={stroke}
            shadow={shadow}
          />
        </AnimationComponent>
      </div>
    </HotspotTrigger>
  );
}

export const Shape = ({ url, width, height, fill, stroke, flip, shadow }) => {
  const elementRef = React.useRef(null);
  const isVisible = useOnScreen(elementRef)

  const fillColor = getColorsConfig(fill);
  const strokeColor = getColorsConfig(_.get(stroke, 'color'));
  const strokeWidth = parseInt(_.get(stroke, 'width', 0));
  const horizontalFlip = _.get(flip, 'horizontal');
  const verticalFlip = _.get(flip, 'vertical');
  const rotate = getFlipStyles(horizontalFlip, verticalFlip);

  const style = {
    fill: fillColor,
    stroke: strokeColor,
    strokeWidth: strokeWidth,
    height: height,
    width: width,
    transform: rotate
  }

  useEffect(() => {
    calculateViewBox();
  }, [width, height, strokeWidth])

  const calculateViewBox = () => {
    if (strokeWidth <= 0 || !elementRef.current || !isVisible) {
      return;
    }

    const graphicElement = elementRef.current.querySelector("g");
    const box = graphicElement.getBBox();
    const viewBox = [-strokeWidth / 2, -strokeWidth / 2, box.width + strokeWidth, box.height + strokeWidth];
    elementRef.current.setAttribute('viewBox', viewBox.join(" "));
  }

  return (
    <ReactSVG
      className={styles.svgWrapper}
      width={width}
      height={height}
      src={url}
      style={style}
      afterInjection={(error, svg) => {
        elementRef.current = svg;
        calculateViewBox();
      }}
    />
  );
}

const shapeWidgetEquals = function (prevProps, nextProps) {
  const paths = ['className', 'style', 'widgetProps', 'breakpoint'];
  const next = _.pick(nextProps, paths);
  const previous = _.pick(prevProps, paths);

  const equals = _.isEqual(next, previous);
  return equals;
}

export const ShapeWidget = React.memo(ShapeWidgetComponent, shapeWidgetEquals);


function getStyles(properties, active) {
  var styles = {};

  if (!properties) {
    return styles;
  }

  //fill-color
  const fillRgb = _.get(properties, 'fill', null);
  const fill = getFillStyles(fillRgb);
  styles = {
    ...styles,
    ...fill
  }

  //stroke-color
  const strokeRgb = _.get(properties, 'stroke', null);
  const stroke = getStrokeStyles(strokeRgb);
  styles = {
    ...styles,
    ...stroke
  }

  //DropShadow
  const shadow = _.get(properties, 'shadow', {});
  const shadowStyle = getDropShadow(shadow);
  styles = {
    ...styles,
    ...shadowStyle
  }

  return styles;

}

//flip-shape
export function getFlipStyles(horizontal, vertical) {
  var transform = '';

  if(!vertical && !horizontal) {
    return 'rotate(0deg)';
  }

  if(vertical) {
    transform = 'rotateX(180deg)';
  }

  if(horizontal) {
    transform = `${transform} rotateY(180deg)`
  }

  return transform;
}

export function getFillStyles(value) {
  if (!value) {
    return null;
  }

  const fillRgb = value;

  var fill = 'inherit';
  if (fillRgb) {
    fill = `rgba(${fillRgb.r}, ${fillRgb.g}, ${fillRgb.b}, ${fillRgb.a})`;
  }

  var styles = {
    fill: fill
  };

  return styles;
}

export function getStrokeStyles(value) {
  if (!value) {
    return null;
  }

  const strokeRgb = _.get(value, 'color');
  const strokeWidth = _.get(value, 'width', 0);

  var stroke = 'inherit';
  if (strokeRgb) {
    stroke = `rgba(${strokeRgb.r}, ${strokeRgb.g}, ${strokeRgb.b}, ${strokeRgb.a})`;
  }

  var styles = {
    'stroke': stroke,
    'strokeWidth': strokeWidth
  };

  return styles;
}
